import { supabase } from '../../../lib/supabase';

import { analyticsService } from './analyticsService';

export interface ABTest {
  name: string;
  variants: string[];
  description: string;
  isActive: boolean;
  startDate?: Date;
  endDate?: Date;
  trafficAllocation?: number; // Percentage of users to include in test (0-100)
}

export interface ABTestAssignment {
  testName: string;
  variant: string;
  assignedAt: Date;
  properties?: Record<string, unknown>;
}

export interface ABTestResult {
  testName: string;
  variant: string;
  metrics: {
    users: number;
    conversions: number;
    conversionRate: number;
    revenue?: number;
    confidence?: number;
  };
}

export class ABTestingService {
  private userAssignments: Map<string, string> = new Map();

  /**
   * Get or assign A/B test variant for current user
   */
  async getVariant(testName: string, variants: string[], userId?: string): Promise<string> {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          // Return default variant for non-authenticated users
          return variants[0];
        }
        userId = user.id;
      }

      // Check cache first
      const cacheKey = `${userId}_${testName}`;
      if (this.userAssignments.has(cacheKey)) {
        return this.userAssignments.get(cacheKey)!;
      }

      // Get assignment from database using RPC function
      const { data: variant, error } = await supabase
        .rpc('get_user_ab_test_variant', {
          p_user_id: userId,
          p_test_name: testName,
          p_variants: variants
        });

      if (error) {
        console.error('Failed to get A/B test variant:', error);
        return variants[0]; // Return default variant on error
      }

      // Cache the assignment
      this.userAssignments.set(cacheKey, variant);

      // Track the assignment
      await analyticsService.trackABTest(testName, variant, {
        userId,
        variants,
        timestamp: new Date().toISOString()
      });

      return variant;

    } catch (error) {
      console.error('Failed to get A/B test variant:', error);
      return variants[0]; // Return default variant on error
    }
  }

  /**
   * Specific A/B tests for Phase 2 implementation
   */
  async getUpgradePromptTiming(): Promise<'after_limit' | 'after_third' | 'after_first'> {
    return await this.getVariant('upgrade_prompt_timing', [
      'after_limit',   // Control: Show after hitting 5-quiz limit
      'after_third',   // Variant A: Show after 3rd quiz
      'after_first'    // Variant B: Show after 1st quiz
    ]) as 'after_limit' | 'after_third' | 'after_first';
  }

  async getSubscriptionPlanDisplay(): Promise<'single_premium' | 'household_highlighted' | 'annual_highlighted'> {
    return await this.getVariant('subscription_plan_display', [
      'single_premium',        // Control: one Premium plan
      'household_highlighted', // Variant A: emphasize household sharing
      'annual_highlighted'     // Variant B: emphasize yearly savings
    ]) as 'single_premium' | 'household_highlighted' | 'annual_highlighted';
  }

  async getPricingStrategy(): Promise<'standard' | 'lower' | 'higher_with_savings'> {
    return await this.getVariant('pricing_strategy', [
      'standard',            // Control: $4.99/month, $49.99/year
      'lower',              // Variant A: future lower-price test
      'higher_with_savings' // Variant B: future premium-anchor test with stronger annual framing
    ]) as 'standard' | 'lower' | 'higher_with_savings';
  }

  /**
   * Track conversion event for A/B test analysis
   */
  async trackConversion(testName: string, conversionType: string, value?: number, userId?: string): Promise<void> {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        userId = user.id;
      }

      // Get user's variant for this test
      const cacheKey = `${userId}_${testName}`;
      let variant = this.userAssignments.get(cacheKey);

      if (!variant) {
        // Try to get from database
        const { data: assignment } = await supabase
          .from('user_ab_tests')
          .select('variant')
          .eq('user_id', userId)
          .eq('test_name', testName)
          .single();

        variant = assignment?.variant || 'unknown';
      }

      // Track conversion event
      await analyticsService.track('ab_test_conversion', {
        testName,
        variant,
        conversionType,
        value,
        userId,
        timestamp: new Date().toISOString()
      });

      // Store conversion in dedicated table for analysis
      await supabase
        .from('ab_test_conversions')
        .insert({
          test_name: testName,
          variant: variant,
          user_id: userId,
          conversion_type: conversionType,
          value: value,
          timestamp: new Date().toISOString()
        });

    } catch (error) {
      console.error('Failed to track A/B test conversion:', error);
    }
  }

  /**
   * Get A/B test results for analysis
   */
  async getTestResults(testName: string, startDate?: Date, endDate?: Date): Promise<ABTestResult[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_ab_test_results', {
          p_test_name: testName,
          p_start_date: startDate?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          p_end_date: endDate?.toISOString() || new Date().toISOString()
        });

      if (error) {
        console.error('Failed to get A/B test results:', error);
        return [];
      }

      return data || [];

    } catch (error) {
      console.error('Failed to get A/B test results:', error);
      return [];
    }
  }

  /**
   * Force assign user to specific variant (for testing)
   */
  async forceAssignVariant(testName: string, variant: string, userId?: string): Promise<void> {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        userId = user.id;
      }

      // Update assignment in database
      await supabase
        .from('user_ab_tests')
        .upsert({
          user_id: userId,
          test_name: testName,
          variant: variant,
          assigned_at: new Date().toISOString(),
          properties: { forced_assignment: true }
        });

      // Update cache
      const cacheKey = `${userId}_${testName}`;
      this.userAssignments.set(cacheKey, variant);

      // Track the forced assignment
      await analyticsService.trackABTest(testName, variant, {
        userId,
        forcedAssignment: true,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('Failed to force assign A/B test variant:', error);
    }
  }

  /**
   * Get all active tests for a user
   */
  async getUserTests(userId?: string): Promise<ABTestAssignment[]> {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        userId = user.id;
      }

      const { data, error } = await supabase
        .from('user_ab_tests')
        .select('test_name, variant, assigned_at, properties')
        .eq('user_id', userId);

      if (error) {
        console.error('Failed to get user A/B tests:', error);
        return [];
      }

      return (data || []).map(assignment => ({
        testName: assignment.test_name,
        variant: assignment.variant,
        assignedAt: new Date(assignment.assigned_at),
        properties: assignment.properties
      }));

    } catch (error) {
      console.error('Failed to get user A/B tests:', error);
      return [];
    }
  }

  /**
   * Clear cache (useful for testing)
   */
  clearCache(): void {
    this.userAssignments.clear();
  }

  /**
   * Check if user should be included in A/B test based on traffic allocation
   */
  private shouldIncludeInTest(userId: string, testName: string, trafficAllocation: number): boolean {
    if (trafficAllocation >= 100) return true;
    
    // Use deterministic hash to ensure consistent inclusion
    const hash = this.hashString(userId + testName);
    const percentage = (hash % 100) + 1;
    return percentage <= trafficAllocation;
  }

  /**
   * Simple hash function for deterministic assignment
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }
}

// Singleton instance
export const abTestingService = new ABTestingService();

// Default export
export default abTestingService;
